/**
 * Simple rule evaluator for form conditional visibility
 * Rule schema (as written by the builder):
 * { joiner: 'AND'|'OR', conditions: [ { field, op, value, valueType? } ] }
 * op: equals, notEquals, in, notIn, lt, lte, gt, gte, exists, contains, regex
 * valueType: 'age' => compare the calculated age (years) of a date field instead of the raw date
 *
 * NOTE: legacy/alternate names (`condition.operator`, `rule.op`) are still accepted as fallbacks.
 */
import moment from 'moment';

/**
 * Compute a person's age in whole years from a date value.
 * Accepts a moment object or a date string in the common form-builder formats.
 * Returns NaN for empty/invalid input so numeric comparisons resolve to false
 * (i.e. an age-gated field stays hidden until a valid date is entered).
 * @param {*} val
 * @returns {number}
 */
function computeAge(val) {
    if (val === undefined || val === null || val === '') return NaN

    let m
    if (moment.isMoment(val)) {
        m = val
    } else {
        m = moment(val, ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD', 'MM-DD-YYYY', moment.ISO_8601], true)
        if (!m.isValid()) {
            // last-resort: let the native Date parser try
            m = moment(new Date(val))
        }
    }

    if (!m || !m.isValid()) return NaN
    return moment().diff(m, 'years')
}

function _compare(val, operator, expected) {
    switch (operator) {
        case 'equals': return val === expected
        case 'notEquals': return val !== expected
        case 'in': return Array.isArray(expected) && expected.indexOf(val) !== -1
        case 'notIn': return Array.isArray(expected) && expected.indexOf(val) === -1
        case 'lt': return Number(val) < Number(expected)
        case 'lte': return Number(val) <= Number(expected)
        case 'gt': return Number(val) > Number(expected)
        case 'gte': return Number(val) >= Number(expected)
        case 'exists': return (val !== undefined && val !== null && val !== '')
        case 'contains': return (Array.isArray(val) && val.indexOf(expected) !== -1) || (typeof val === 'string' && String(val).indexOf(String(expected)) !== -1)
        case 'regex': {
            try {
                const re = new RegExp(expected)
                return re.test(String(val))
            } catch (e) {
                return false
            }
        }
        default:
            return false
    }
}

function evaluateCondition(condition, valueContainer) {
    if (!condition || !condition.field) return false
    const field = condition.field
    // Builder saves `op`; keep `operator` as a fallback for the documented schema.
    const operator = condition.op || condition.operator || 'equals'

    let val = valueContainer && Object.prototype.hasOwnProperty.call(valueContainer, field) ? valueContainer[field] : undefined
    let expected = condition.value

    // "Compare as age": transform the date value into an age in years, and coerce the
    // expected value to a number so even `equals` compares number-vs-number.
    if (condition.valueType === 'age') {
        val = computeAge(val)
        if (expected !== '' && expected !== null && expected !== undefined) {
            expected = Number(expected)
        }
    }

    return _compare(val, operator, expected)
}

function evaluateRule(rule, valueContainer) {
    if (!rule) return true
    // Builder saves the joiner as `joiner` ('AND'|'OR'); keep `op` as a fallback.
    const op = (rule.joiner || rule.op || 'and').toLowerCase()
    const conditions = Array.isArray(rule.conditions) ? rule.conditions : []

    if (!conditions.length) return true

    if (op === 'or') {
        return conditions.some(c => evaluateCondition(c, valueContainer))
    }

    // default AND
    return conditions.every(c => evaluateCondition(c, valueContainer))
}

export { evaluateRule }
